import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export interface DynamicsParameters {
  readonly thresholdDb: number;
  readonly ratio: number;
  readonly kneeDb: number;
  readonly attackMs: number;
  readonly releaseMs: number;
  readonly makeupDb: number;
  readonly lookaheadMs: number;
  readonly limiterCeilingDb: number;
  readonly limiterReleaseMs: number;
  readonly softClipDrive: number;
}

export interface DynamicsProcessorOptions extends Partial<DynamicsParameters> {
  readonly sampleRate: number;
}

type ParameterKey = keyof DynamicsParameters;
type ParameterId = 'threshold-db' | 'ratio' | 'knee-db' | 'attack-ms' | 'release-ms' | 'makeup-db'
  | 'lookahead-ms' | 'limiter-ceiling-db' | 'limiter-release-ms' | 'soft-clip-drive';

export const PARAMETER_BOUNDS = Object.freeze({
  thresholdDb: Object.freeze({ min: -80, max: 0, unit: 'dB' }),
  ratio: Object.freeze({ min: 1, max: 100, unit: 'ratio' }),
  kneeDb: Object.freeze({ min: 0, max: 40, unit: 'dB' }),
  attackMs: Object.freeze({ min: 0, max: 500, unit: 'ms' }),
  releaseMs: Object.freeze({ min: 0, max: 5_000, unit: 'ms' }),
  makeupDb: Object.freeze({ min: -24, max: 24, unit: 'dB' }),
  lookaheadMs: Object.freeze({ min: 0, max: 20, unit: 'ms' }),
  limiterCeilingDb: Object.freeze({ min: -24, max: 0, unit: 'dBTP' }),
  limiterReleaseMs: Object.freeze({ min: 1, max: 2_000, unit: 'ms' }),
  softClipDrive: Object.freeze({ min: 0, max: 8, unit: 'ratio' }),
} as const);

export const DEFAULT_DYNAMICS_PARAMETERS: Readonly<DynamicsParameters> = Object.freeze({
  thresholdDb: -18,
  ratio: 4,
  kneeDb: 6,
  attackMs: 10,
  releaseMs: 120,
  makeupDb: 0,
  lookaheadMs: 5,
  limiterCeilingDb: -1,
  limiterReleaseMs: 80,
  softClipDrive: 1,
});

const ID_TO_KEY: Readonly<Record<ParameterId, ParameterKey>> = Object.freeze({
  'threshold-db': 'thresholdDb',
  ratio: 'ratio',
  'knee-db': 'kneeDb',
  'attack-ms': 'attackMs',
  'release-ms': 'releaseMs',
  'makeup-db': 'makeupDb',
  'lookahead-ms': 'lookaheadMs',
  'limiter-ceiling-db': 'limiterCeilingDb',
  'limiter-release-ms': 'limiterReleaseMs',
  'soft-clip-drive': 'softClipDrive',
});

const dbToGain = (value: number): number => 10 ** (value / 20);
const gainToDb = (value: number): number => 20 * Math.log10(Math.max(value, 1e-30));
const coefficient = (milliseconds: number, sampleRate: number): number => milliseconds === 0 ? 0 : Math.exp(-1 / (milliseconds * 0.001 * sampleRate));

const assertRange = (label: string, value: number, minimum: number, maximum: number): void => {
  if (!Number.isFinite(value) || value < minimum || value > maximum) throw new RangeError(`${label} must be in [${minimum}, ${maximum}]`);
};

/** Static compressor gain reduction (negative dB), using the Audio EQ Cookbook soft-knee law. */
export const compressorGainDb = (inputDb: number, thresholdDb: number, ratio: number, kneeDb: number): number => {
  assertRange('ratio', ratio, PARAMETER_BOUNDS.ratio.min, PARAMETER_BOUNDS.ratio.max);
  assertRange('kneeDb', kneeDb, PARAMETER_BOUNDS.kneeDb.min, PARAMETER_BOUNDS.kneeDb.max);
  if (!Number.isFinite(inputDb) || !Number.isFinite(thresholdDb)) throw new RangeError('levels must be finite');
  const slope = 1 / ratio - 1;
  if (kneeDb === 0) return inputDb <= thresholdDb ? 0 : slope * (inputDb - thresholdDb);
  const lower = thresholdDb - kneeDb * 0.5;
  const upper = thresholdDb + kneeDb * 0.5;
  if (inputDb <= lower) return 0;
  if (inputDb >= upper) return slope * (inputDb - thresholdDb);
  const position = inputDb - lower;
  return slope * position * position / (2 * kneeDb);
};

const interpolate4 = (a: number, b: number, c: number, d: number, t: number): number => {
  const l0 = -t * (t - 1) * (t - 2) / 6;
  const l1 = (t + 1) * (t - 1) * (t - 2) / 2;
  const l2 = -(t + 1) * t * (t - 2) / 2;
  const l3 = (t + 1) * t * (t - 1) / 6;
  return a * l0 + b * l1 + c * l2 + d * l3;
};

/** Deterministic 4-point/4x inter-sample peak estimate (not a metering-standard certified dBTP meter). */
export const estimateTruePeak = (input: Float32Array, oversample = 4): number => {
  if (!Number.isInteger(oversample) || oversample < 1 || oversample > 16) throw new RangeError('oversample must be an integer in [1, 16]');
  let peak = 0;
  for (let index = 0; index < input.length; index += 1) {
    const sample = input[index]!;
    if (!Number.isFinite(sample)) throw new RangeError('input samples must be finite');
    peak = Math.max(peak, Math.abs(sample));
    if (index < 1 || index + 1 >= input.length) continue;
    const a = input[Math.max(0, index - 1)]!;
    const b = sample;
    const c = input[index + 1]!;
    const d = input[Math.min(input.length - 1, index + 2)]!;
    for (let phase = 1; phase < oversample; phase += 1) peak = Math.max(peak, Math.abs(interpolate4(a, b, c, d, phase / oversample)));
  }
  return peak;
};

export class DynamicsProcessor {
  readonly sampleRate: number;
  private readonly values: DynamicsParameters;
  private readonly delay: Float64Array;
  private readonly peakValues: Float64Array;
  private readonly peakIndices: Float64Array;
  private delayWrite = 0;
  private queueHead = 0;
  private queueTail = 0;
  private sampleIndex = 0;
  private compressorGainDbState = 0;
  private limiterGain = 1;
  private history0 = 0;
  private history1 = 0;
  private history2 = 0;
  private historyCount = 0;
  private observedTruePeak = 0;

  constructor(options: DynamicsProcessorOptions) {
    assertRange('sampleRate', options.sampleRate, 1, 768_000);
    this.sampleRate = options.sampleRate;
    this.values = { ...DEFAULT_DYNAMICS_PARAMETERS, ...options };
    for (const key of Object.keys(PARAMETER_BOUNDS) as ParameterKey[]) {
      const bounds = PARAMETER_BOUNDS[key];
      assertRange(key, this.values[key], bounds.min, bounds.max);
    }
    const maximumLookahead = Math.ceil(PARAMETER_BOUNDS.lookaheadMs.max * 0.001 * this.sampleRate);
    this.delay = new Float64Array(maximumLookahead + 1);
    this.peakValues = new Float64Array(maximumLookahead + 2);
    this.peakIndices = new Float64Array(maximumLookahead + 2);
  }

  get latencySamples(): number { return Math.round(this.values.lookaheadMs * 0.001 * this.sampleRate); }
  get latencySeconds(): number { return this.latencySamples / this.sampleRate; }
  get compressorGainReductionDb(): number { return -this.compressorGainDbState; }
  get limiterGainReductionDb(): number { return -gainToDb(this.limiterGain); }
  get truePeakEstimate(): number { return this.observedTruePeak; }

  reset(): void {
    this.delay.fill(0);
    this.peakValues.fill(0);
    this.peakIndices.fill(0);
    this.delayWrite = 0;
    this.queueHead = 0;
    this.queueTail = 0;
    this.sampleIndex = 0;
    this.compressorGainDbState = 0;
    this.limiterGain = 1;
    this.history0 = 0;
    this.history1 = 0;
    this.history2 = 0;
    this.historyCount = 0;
    this.observedTruePeak = 0;
  }

  setParameter(id: string, value: number): void {
    const key = ID_TO_KEY[id as ParameterId];
    if (!key) throw new RangeError(`unknown parameter: ${id}`);
    const bounds = PARAMETER_BOUNDS[key];
    assertRange(id, value, bounds.min, bounds.max);
    (this.values as { -readonly [K in ParameterKey]: number })[key] = value;
  }

  processInto(input: Float32Array, output: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 0 || input.length < frames || output.length < frames) throw new RangeError('buffers are too short for frames');
    for (let frame = 0; frame < frames; frame += 1) {
      if (!Number.isFinite(input[frame]!)) throw new RangeError('input samples must be finite');
    }
    const attack = coefficient(this.values.attackMs, this.sampleRate);
    const release = coefficient(this.values.releaseMs, this.sampleRate);
    const limiterRelease = coefficient(this.values.limiterReleaseMs, this.sampleRate);
    const makeup = this.values.makeupDb;
    const ceiling = dbToGain(this.values.limiterCeilingDb);
    const lookahead = this.latencySamples;
    const queueCapacity = this.peakValues.length;

    for (let frame = 0; frame < frames; frame += 1) {
      const sample = input[frame]!;
      const inputDb = gainToDb(Math.abs(sample));
      const targetDb = compressorGainDb(inputDb, this.values.thresholdDb, this.values.ratio, this.values.kneeDb);
      const smoothing = targetDb < this.compressorGainDbState ? attack : release;
      this.compressorGainDbState = smoothing * this.compressorGainDbState + (1 - smoothing) * targetDb;
      const compressed = sample * dbToGain(this.compressorGainDbState + makeup);

      this.delay[this.delayWrite] = compressed;
      let detectorPeak = Math.abs(compressed);
      if (this.historyCount >= 3) {
        for (let phase = 1; phase < 4; phase += 1) detectorPeak = Math.max(detectorPeak, Math.abs(interpolate4(this.history0, this.history1, this.history2, compressed, phase / 4)));
      }
      this.history0 = this.history1;
      this.history1 = this.history2;
      this.history2 = compressed;
      if (this.historyCount < 3) this.historyCount += 1;
      this.observedTruePeak = Math.max(this.observedTruePeak, detectorPeak);

      while (this.queueHead < this.queueTail && this.peakValues[(this.queueTail - 1) % queueCapacity]! <= detectorPeak) this.queueTail -= 1;
      this.peakValues[this.queueTail % queueCapacity] = detectorPeak;
      this.peakIndices[this.queueTail % queueCapacity] = this.sampleIndex;
      this.queueTail += 1;
      const oldest = this.sampleIndex - lookahead;
      while (this.queueHead < this.queueTail && this.peakIndices[this.queueHead % queueCapacity]! < oldest) this.queueHead += 1;
      if (this.queueHead >= queueCapacity) { this.queueHead -= queueCapacity; this.queueTail -= queueCapacity; }
      const windowPeak = this.peakValues[this.queueHead % queueCapacity]!;
      const targetLimiter = windowPeak > ceiling ? ceiling / windowPeak : 1;
      this.limiterGain = targetLimiter < this.limiterGain ? targetLimiter : limiterRelease * this.limiterGain + (1 - limiterRelease);

      const readIndex = (this.delayWrite - lookahead + this.delay.length) % this.delay.length;
      const limited = this.delay[readIndex]! * this.limiterGain;
      output[frame] = this.softClip(limited, ceiling);
      this.delayWrite = (this.delayWrite + 1) % this.delay.length;
      this.sampleIndex += 1;
    }
  }

  private softClip(value: number, ceiling: number): number {
    const absolute = Math.abs(value);
    if (absolute >= ceiling) return Math.sign(value) * ceiling;
    const drive = this.values.softClipDrive;
    if (drive === 0) return value;
    const start = ceiling * Math.max(0.5, 1 - 0.05 * drive);
    if (absolute <= start) return value;
    const width = ceiling - start;
    return Math.sign(value) * (start + width * Math.tanh((absolute - start) / width));
  }
}

class DynamicsModuleInstance implements DspModuleInstance {
  private readonly processor: DynamicsProcessor;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.processor = new DynamicsProcessor({ sampleRate }); }
  reset(): void { this.processor.reset(); }
  setParameter(id: string, value: number): void { this.processor.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    if (!Number.isInteger(block.frames) || block.frames < 0 || block.frames > this.maxBlockSize) throw new RangeError('frames exceeds the configured maximum block size');
    const input = block.inputs.in;
    const output = block.outputs.out;
    if (!input || input.length < block.frames) throw new RangeError('in buffer is missing or too short');
    if (!output || output.length < block.frames) throw new RangeError('out buffer is missing or too short');
    this.processor.processInto(input, output, block.frames);
  }
}

export const mod025 = defineDspModule({
  descriptor: {
    id: 'mir.mod-025', name: 'Compressor, lookahead limiter and soft-clip safety', version: '0.1.0',
    inputs: [{ id: 'in', kind: 'audio', channels: 1 }],
    outputs: [{ id: 'out', kind: 'audio', channels: 1 }],
    parameters: [
      { id: 'threshold-db', defaultValue: -18, minValue: -80, maxValue: 0, unit: 'dB' },
      { id: 'ratio', defaultValue: 4, minValue: 1, maxValue: 100, unit: 'ratio' },
      { id: 'knee-db', defaultValue: 6, minValue: 0, maxValue: 40, unit: 'dB' },
      { id: 'attack-ms', defaultValue: 10, minValue: 0, maxValue: 500, unit: 'ms' },
      { id: 'release-ms', defaultValue: 120, minValue: 0, maxValue: 5_000, unit: 'ms' },
      { id: 'makeup-db', defaultValue: 0, minValue: -24, maxValue: 24, unit: 'dB' },
      { id: 'lookahead-ms', defaultValue: 5, minValue: 0, maxValue: 20, unit: 'ms' },
      { id: 'limiter-ceiling-db', defaultValue: -1, minValue: -24, maxValue: 0, unit: 'dBTP' },
      { id: 'limiter-release-ms', defaultValue: 80, minValue: 1, maxValue: 2_000, unit: 'ms' },
      { id: 'soft-clip-drive', defaultValue: 1, minValue: 0, maxValue: 8, unit: 'ratio' },
    ],
  },
  createInstance(context) {
    if (!Number.isFinite(context.sampleRate) || context.sampleRate <= 0 || !Number.isInteger(context.maxBlockSize) || context.maxBlockSize < 1) throw new RangeError('invalid DSP instance context');
    return new DynamicsModuleInstance(context.sampleRate, context.maxBlockSize);
  },
});
